import { lazy, ComponentType } from 'react';

const RETRY_DELAY_MS = 1500;
const RELOAD_GUARD_KEY = 'chunk_reload_at';
const RELOAD_GUARD_WINDOW_MS = 60_000;

const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  if (err.name === 'ChunkLoadError') return true;
  if (typeof err.message === 'string' && /Loading chunk .* failed/i.test(err.message)) return true;
  return false;
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wraps React.lazy's dynamic import factory with retry + chunk-load-error
 * recovery. With many screens kept mounted simultaneously (KeepAliveOutlet),
 * lazy chunk requests can time out under connection pressure; React.lazy
 * caches a failed import forever, leaving the screen stuck on its Suspense
 * fallback until a manual F5. This retries once, and if the failure is a
 * genuine stale-chunk error (deployed a new build, browser has old chunk
 * manifest), does a single guarded full reload instead of failing forever.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch((firstError) => {
      // A non-chunk failure here is almost always a module-init throw in the
      // screen's import graph. Retrying returns webpack's half-initialised
      // module and the real error is replaced by an opaque
      // "Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization",
      // so log the original before it is lost.
      if (!isChunkLoadError(firstError)) {
        // eslint-disable-next-line no-console
        console.error('[lazyWithRetry] module failed to initialise:', firstError);
      }
      return wait(RETRY_DELAY_MS)
        .then(factory)
        .catch((secondError) => {
          // Only auto-reload in production. In dev the watcher recompiles
          // constantly and chunk errors are routine — a forced full reload
          // would lose testers' open tabs for no reason. Let the
          // ErrorBoundary show instead.
          if (isChunkLoadError(secondError) && process.env.NODE_ENV === 'production') {
            let lastReloadAt = 0;
            try {
              lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
            } catch {
              // sessionStorage unavailable — fall through to rethrow
            }

            const now = Date.now();
            if (!lastReloadAt || now - lastReloadAt > RELOAD_GUARD_WINDOW_MS) {
              try {
                sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
              } catch {
                // ignore — reload can still proceed
              }
              window.location.reload();
              // Never resolve — the page is about to reload, don't flash
              // an error boundary in the meantime.
              return new Promise<{ default: T }>(() => {});
            }
          }

          // Either not a chunk error, or we already tried a reload recently
          // (e.g. reload itself hits the same failure) — surface the error
          // so ErrorBoundary can show its fallback instead of looping.
          throw secondError;
        });
    })
  );
}

export default lazyWithRetry;
